/**
 * 应用更新操作 hook
 * 提供 handleUpdatePrimaryAction（下载/安装更新）和 handleManualCheckUpdate（手动检查更新）。
 * 供 ForceUpdateOverlay 和 MainLayout 共同使用。
 */

import type { MessageInstance } from 'antd/es/message/interface';
import { IPC } from '../../common/ipc/channels';
import { APP_REPORT_EVENTS } from '../../common/enums/appReport';
import { appReporter } from '../utils/appReporter';
import { useAppUpdateStore } from '../store/appUpdate';

/** 构造版本追踪 key */
function buildUpdateTrackingKey(status?: { latestVersion?: string; latestBuildVersion?: number } | null) {
  return [
    status?.latestVersion || '',
    status?.latestBuildVersion || '',
  ].join(':');
}

export function useAppUpdateActions(messageApi: MessageInstance) {
  const handleUpdatePrimaryAction = async () => {
    if (!window.electronAPI) {
      return;
    }

    const state = useAppUpdateStore.getState();
    if (state.updateActionSubmitting) {
      return;
    }

    const effectiveUpdateStatus = state.pendingUpdateStatus ?? state.downloadingUpdateStatus ?? state.availableUpdateStatus;
    const isForceUpdate = effectiveUpdateStatus?.forceUpdate === true || state.forceUpdateFlowActive;

    // 已下载 → 安装
    if (state.pendingUpdateStatus?.status === 'downloaded') {
      useAppUpdateStore.setState({
        updateModalOpen: false,
        updateActionSubmitting: true,
        forceUpdateFlowError: '',
      });
      void appReporter.reportUpdateEvent(APP_REPORT_EVENTS.UPDATE_INSTALL_CLICK, {
        latestVersion: state.pendingUpdateStatus.latestVersion || '',
        latestBuildVersion: state.pendingUpdateStatus.latestBuildVersion ?? '',
        forceUpdate: Boolean(state.pendingUpdateStatus.forceUpdate),
        status: state.pendingUpdateStatus.status,
      });
      try {
        await window.electronAPI.invoke(IPC.INSTALL_APP_UPDATE);
        void appReporter.reportUpdateEvent(APP_REPORT_EVENTS.UPDATE_INSTALL_START, {
          latestVersion: state.pendingUpdateStatus.latestVersion || '',
          latestBuildVersion: state.pendingUpdateStatus.latestBuildVersion ?? '',
          forceUpdate: Boolean(state.pendingUpdateStatus.forceUpdate),
          status: state.pendingUpdateStatus.status,
          platform: window.electronAPI.platform === 'win32' ? 'windows' : 'mac',
        });
        messageApi.success('已开始安装更新');
      } catch (error) {
        useAppUpdateStore.setState({ updateActionSubmitting: false });
        messageApi.error(error instanceof Error ? error.message : '启动更新安装失败');
      }
      return;
    }

    // 未下载 → 开始下载
    useAppUpdateStore.setState({
      updateModalOpen: false,
      updateActionSubmitting: true,
      ...(isForceUpdate ? { forceUpdateFlowActive: true, forceUpdateFlowError: '' } : {}),
    });
    try {
      if (effectiveUpdateStatus) {
        void appReporter.reportUpdateEvent(APP_REPORT_EVENTS.UPDATE_DOWNLOAD_START, {
          latestVersion: effectiveUpdateStatus.latestVersion || '',
          latestBuildVersion: effectiveUpdateStatus.latestBuildVersion ?? '',
          forceUpdate: Boolean(effectiveUpdateStatus.forceUpdate),
          status: effectiveUpdateStatus.status,
        });
      }
      const result = await window.electronAPI.invoke(IPC.DOWNLOAD_APP_UPDATE);
      if (!result?.started) {
        useAppUpdateStore.setState({ updateActionSubmitting: false });
        messageApi.info('更新包正在准备中，请稍候');
      }
    } catch (error) {
      const trackingKey = buildUpdateTrackingKey(effectiveUpdateStatus);
      if (trackingKey) {
        void appReporter.reportUpdateEvent(APP_REPORT_EVENTS.UPDATE_DOWNLOAD_RESULT, {
          latestVersion: effectiveUpdateStatus?.latestVersion || '',
          latestBuildVersion: effectiveUpdateStatus?.latestBuildVersion ?? '',
          forceUpdate: Boolean(effectiveUpdateStatus?.forceUpdate),
          result: 'fail',
          reasonCode: 'download_start_failed',
          reasonMessage: error instanceof Error ? error.message : '启动更新下载失败',
        });
      }
      useAppUpdateStore.setState({ updateActionSubmitting: false });
      messageApi.error(error instanceof Error ? error.message : '启动更新下载失败');
    }
  };

  const handleManualCheckUpdate = async () => {
    if (!window.electronAPI) {
      return;
    }

    const state = useAppUpdateStore.getState();
    if (state.manualUpdateChecking || state.updateActionSubmitting) {
      return;
    }

    useAppUpdateStore.setState({ manualUpdateChecking: true });
    try {
      const result = await window.electronAPI.invoke(IPC.CHECK_APP_UPDATE, { manual: true });
      if (!result?.queued) {
        useAppUpdateStore.setState({ manualUpdateChecking: false });
        if (result?.reason === 'dev_update_disabled') {
          messageApi.info('当前是开发环境，请先开启更新调试后再检查更新');
        } else {
          messageApi.info('检查更新任务已存在，请稍候');
        }
      }
    } catch (error) {
      useAppUpdateStore.setState({ manualUpdateChecking: false });
      messageApi.error(error instanceof Error ? error.message : '检查更新失败，请稍后重试');
    }
  };

  return { handleUpdatePrimaryAction, handleManualCheckUpdate };
}
